import React, { useMemo, useState } from 'react';

import moment from 'moment';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { CalendarAssignmentEmployee, ShiftInterfere } from 'types/interfaces';

import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

const validationDialogContentStyle: React.CSSProperties = {
  maxHeight: 'calc(100vh - 8rem)',
  overflow: 'auto'
};

export const ValidationErrorsDialog: React.FC<{
  visible: boolean;
  errors: ShiftInterfere[];
  employees: CalendarAssignmentEmployee[];
  onHide: () => void;
}> = ({ visible, errors, employees, onHide }) => {
  // Состояние для раскрытых строк
  const [expandedRows, setExpandedRows] =
    useState<DataTableExpandedRows | null>(null);

  // Группируем ошибки по сотрудникам
  const groupedData = useMemo(() => {
    const groups: {
      id: number;
      employee: CalendarAssignmentEmployee | null;
      errors: ShiftInterfere[];
    }[] = [];

    errors.forEach((error) => {
      const existingGroup = groups.find((g) => g.id === error.personId);
      if (existingGroup) {
        existingGroup.errors.push(error);
      } else {
        const employee = employees.find((e) => e.id === error.personId) || null;
        groups.push({
          id: error.personId,
          employee,
          errors: [error]
        });
      }
    });

    return groups;
  }, [errors, employees]);

  // Шаблон для расширенной строки
  const rowExpansionTemplate = (rowData: {
    id: number;
    employee: CalendarAssignmentEmployee | null;
    errors: ShiftInterfere[];
  }) => {
    return (
      <DataTable
        value={rowData.errors}
        scrollable
        className="w-full validationErrorsDialog-innerTable"
      >
        <Column
          field="date"
          header="Дата"
          body={(row) => moment(row.date).format('DD.MM.YYYY')}
        />
        <Column
          field="workdayStart"
          header="Начало смены"
          body={(row) => row.workdayStart}
          style={{ minWidth: '14%', width: '14%' }}
        />
        <Column
          style={{ minWidth: '171px', width: '171px' }}
          field="workdayDuration"
          header="Продолжительность"
          body={(row) => row.workdayDuration}
        />
        <Column
          field="departmentName"
          header="Участок конфликта"
          body={(row) => row.departmentName}
        />
      </DataTable>
    );
  };

  // Тело колонки "Сотрудник"
  const employeeBodyTemplate = (rowData: {
    id: number;
    employee: CalendarAssignmentEmployee | null;
    errors: ShiftInterfere[];
  }) => {
    return rowData.employee ? (
      <div className="flex align-items-center gap-2">
        {rowData.employee.name}
      </div>
    ) : (
      <span>Неизвестный сотрудник</span>
    );
  };

  // Тело колонки "Количество ошибок"
  const errorCountBodyTemplate = (rowData: {
    id: number;
    employee: CalendarAssignmentEmployee | null;
    errors: ShiftInterfere[];
  }) => {
    return (
      <Tag
        severity={rowData.errors.length > 3 ? 'danger' : 'warning'}
        value={rowData.errors.length}
      />
    );
  };

  return (
    <Dialog
      header="Конфликты расписаний сотрудников"
      visible={visible}
      className="calendarAssignments-dialog calendarAssignments-dialog--validation"
      style={{
        width: 'min(800px, calc(100vw - 1rem))',
        maxWidth: 'calc(100vw - 1rem)',
        maxHeight: 'calc(100vh - 1rem)'
      }}
      contentStyle={validationDialogContentStyle}
      onHide={onHide}
      pt={withDialogFooterSpacing()}
      footer={
        <div>
          <Button
            label="Закрыть"
            className="p-button-secondary"
            onClick={onHide}
          />
        </div>
      }
    >
      <Message
        severity="warn"
        text="График пересчитан частично из-за конфликтов"
      />
      <DataTable
        value={groupedData}
        expandedRows={expandedRows!}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={rowExpansionTemplate}
        scrollable
        scrollHeight="400px"
        className="w-full validationErrorsDialog-main-table"
      >
        <Column expander style={{ width: '3em' }} />
        <Column
          field="employee"
          header="Сотрудник"
          body={employeeBodyTemplate}
        />
        <Column
          field="errors"
          header="Пересекающиеся смены"
          body={errorCountBodyTemplate}
        />
      </DataTable>
    </Dialog>
  );
};
